<script lang="ts">
import {
  ArcElement,
  BarController,
  BarElement,
  BubbleController,
  CategoryScale,
  Chart,
  Decimation,
  DoughnutController,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  LogarithmicScale,
  PieController,
  PointElement,
  PolarAreaController,
  RadarController,
  RadialLinearScale,
  ScatterController,
  SubTitle,
  TimeScale,
  TimeSeriesScale,
  Title,
  Tooltip,
} from 'chart.js';
import { defineComponent, h } from 'vue';
import { Bar } from 'vue-chartjs';
import filters from '@/filters';
import { compliantKey } from '@/helpers/compliant-register';
import i18n from '@/i18n';
import { useThemeStore } from '@/stores/theme';

Chart.register(
  ArcElement,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Legend,
  Title,
  Tooltip,
  SubTitle,
);

export default defineComponent({
  name: 'BarLineChart',
  components: { Bar },
  props: {
    chartData: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { theme } = useThemeStore();
    const textColor = theme === 'DARK' ? '#ddd' : '#555';
    const turnoverColor = theme === 'DARK' ? '#374159' : '#051230';

    const chartData = {
      datasets: [
        {
          type: 'line',
          fill: false,
          label: i18n.t(compliantKey('reports.ranges.orders')),
          backgroundColor: '#03cfa5',
          borderColor: '#03cfa5',
          data: props.chartData.data[0],
          xAxisID: 'x0',
          yAxisID: 'y0',
        },
        {
          type: 'bar',
          label: i18n.t('reports.ranges.turnover'),
          backgroundColor: turnoverColor,
          data: props.chartData.data[1],
          xAxisID: 'x0',
          yAxisID: 'y1',
          barPercentage: 0.5,
        },
      ],
      labels: props.chartData.labels,
    };
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      borderRadius: 4,
      scales: {
        y0: {
          title: {
            display: true,
            text: i18n.t(compliantKey('reports.ranges.orders')),
            color: textColor,
          },
          type: 'linear',
          display: true,
          position: 'right',
          grid: {
            display: false,
          },
          ticks: {
            beginAtZero: true,
            stepSize: 1,
            maxTicksLimit: 10,
            color: textColor,
          },
        },
        y1: {
          title: {
            display: true,
            text: i18n.t('reports.ranges.turnover'),
            color: textColor,
          },
          type: 'linear',
          display: true,
          position: 'left',
          ticks: {
            callback: (value) => filters.currency(value * 100),
            beginAtZero: true,
            stepSize: 1,
            maxTicksLimit: 10,
            color: '#fff',
          },
        },
        x0: {
          ticks: {
            beginAtZero: true,
            color: textColor,
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          labels: {
            color: textColor,
          },
        },
      },
      tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (tooltipItem) => {
            if (tooltipItem.datasetIndex === 1) {
              return `${i18n.t('reports.ranges.turnover')}: ${filters.currency(tooltipItem.yLabel * 100)}`;
            }
            return `${i18n.t(compliantKey('reports.ranges.orders'))}: ${tooltipItem.yLabel}`;
          },
        },
      },
    };

    return () =>
      h(Bar, {
        chartData,
        chartOptions,
      });
  },
});
</script>
